import {DocumentNode, gql} from '@apollo/client';
import {groupByUniqueKey, compact} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {assert} from 'Asserts';
import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import {
  doBoxSchemasWithRegistryQuery,
  doPlateSchemasWithRegistryQuery,
  doLocationSchemasWithRegistryQuery,
  doContainerSchemasWithRegistryQuery,
} from 'inventory/useStorableSchemasQuery';
import EditableLookupConfigDisplay from 'lookup-config/EditableLookupConfigDisplay';
import {InferredLookupConfigType} from 'lookup-config/inferTypeFromLookupConfig';
import LookupConfigModal from 'lookup-config/LookupConfigModal';
import {
  LookupConfigSpec,
  SchemaById,
  LookupStepType,
  DEFAULT_INTERMEDIATE_LOOKUP_STEP_TYPES,
  DEFAULT_FINAL_LOOKUP_STEP_TYPES,
} from 'lookup-config/LookupConfigModalConstants';
import {RunLookupConfigModalProps} from 'lookup-config/LookupConfigUtils';
import {isLookupConfigValidForModal} from 'lookup-config/LookupStepInitializerUtils';
import getLookupConfigValidationMessage from 'processes/modals/field-mapping/getLookupConfigValidationMessage';
import {ProcessesTagSchemasInterfacesQuery} from 'processes/modals/utils/__generated/ProcessesTagSchemasInterfacesQuery';
import {doAllResultSchemasQuery} from 'results/useAllResultSchemasQuery';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {Schema, SchemaWithInterfaces} from 'schemas/SchemaConstants';
import {doAllSchemaInterfacesQuery} from 'schemas/util/useAllSchemaInterfacesQuery';
import {doTagSchemasWithRegistryQuery} from 'schemas/util/useTagSchemasQuery';
import getTestProps from 'test/lib/cypress/getTestProps';
import {TooltipWrapper} from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import legacyRunModal from 'ui-library/legacyRunModal';
import {withLoadingScreen} from 'util/Promises';

import {ProcessTaskSchemaGraphQL} from '../../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaGraphQL';
import {
  doAllProcessTaskSchemasForSchemaConfigQuery,
  PROCESSES_TAG_SCHEMAS_INTERFACES_QUERY,
  useAllSchemasById,
} from '../utils/ProcessesAllSchemasQueryUtils';
import {SchemaMapperConfigFetchSelectorsQuery} from './__generated/SchemaMapperConfigFetchSelectorsQuery';

export const SCHEMA_MAPPER_CONFIG_FETCH_SELECTORS_QUERY: DocumentNode = gql`
  query SchemaMapperConfigFetchSelectorsQuery @skipAudit @engComponent(name: WORKFLOWS) {
    schemaFieldSelectors(includeArchived: false) {
      id
      name
    }
  }
`;

interface SchemaFieldLookupProps {
  flowchartTaskSchema: ProcessTaskSchemaGraphQL;
  disabled?: boolean;
  lookupConfig: LookupConfigSpec | null;
  fromSchema: Schema | null;
  toSchemaField: SchemaField;
  onSubmit: (lookupConfig: LookupConfigSpec) => void;
  onRemove: () => void;
}

export async function runLookupConfigModalForProcessSchemaMapper({
  fromSchema,
  lookupConfig,
  flowchartTaskSchema,
  onSubmit,
  allSchemasById,
  toSchemaField,
  getAdditionalLookupConfigValidationMessage,
  canLookupSourceObjectBeModified,
}: {
  fromSchema: Schema;
  toSchemaField: SchemaField;
  flowchartTaskSchema: Schema;
  lookupConfig: LookupConfigSpec | null;
  allSchemasById: SchemaById;
  onSubmit: (lookupConfig: LookupConfigSpec) => void;
  getAdditionalLookupConfigValidationMessage?: (lookupConfig: LookupConfigSpec) => string | null;
  canLookupSourceObjectBeModified: boolean;
}): Promise<void> {
  // Ensure all schemas and their interfaces are loaded
  const relatedSchemaInterfaces = doQuerySafe<ProcessesTagSchemasInterfacesQuery, {}>({
    query: PROCESSES_TAG_SCHEMAS_INTERFACES_QUERY,
  });
  const schemaFieldSelectors = doQuerySafe<SchemaMapperConfigFetchSelectorsQuery, {}>({
    query: SCHEMA_MAPPER_CONFIG_FETCH_SELECTORS_QUERY,
  });
  const supportingData = Promise.all([
    doBoxSchemasWithRegistryQuery(),
    doContainerSchemasWithRegistryQuery(),
    doLocationSchemasWithRegistryQuery(),
    doPlateSchemasWithRegistryQuery(),
    doAllResultSchemasQuery(),
    doAllSchemaInterfacesQuery(),
    doTagSchemasWithRegistryQuery(),
    doAllProcessTaskSchemasForSchemaConfigQuery(),
    relatedSchemaInterfaces,
    schemaFieldSelectors,
  ]);
  const [
    _boxSchemasResult,
    _containerSchemasResult,
    _locationSchemasResult,
    _plateSchemasResult,
    _resultSchemasResult,
    _allSchemaInterfacesResult,
    _tagSchemasResult,
    _processTaskSchemasResult,
    relatedSchemaInterfacesResult,
    schemaFieldSelectorsResult,
  ] = await withLoadingScreen(supportingData);

  // Merge interface information into tag schemas
  relatedSchemaInterfacesResult.tagSchemas.forEach((schema) => {
    allSchemasById[schema.id] = {
      ...(allSchemasById[schema.id] as Schema),
      interfaces: schema.interfaces,
    } as SchemaWithInterfaces;
  });
  const selectorsById = groupByUniqueKey(
    schemaFieldSelectorsResult.schemaFieldSelectors,
    (selector) => selector.id
  );

  const canLookupParentTask =
    canLookupSourceObjectBeModified ||
    lookupConfig?.lookupSteps.some((step) => step.type === LookupStepType.PROCESS_TASK);
  const canLookupRootTask =
    canLookupSourceObjectBeModified ||
    lookupConfig?.lookupSteps.some((step) => step.type === LookupStepType.FLOWCHART_PROCESS_TASK);
  const canLookupSourceField = canLookupSourceObjectBeModified || !(canLookupParentTask || canLookupRootTask);

  const runLookupConfigModalParams: RunLookupConfigModalProps = {
    baseSchema: fromSchema,
    initialLookupConfig: lookupConfig,
    context: {
      flowchartTaskSchema,
    },
    firstLookupStepTypes: compact([
      canLookupSourceField ? LookupStepType.SCHEMA_FIELD : undefined,
      canLookupParentTask ? LookupStepType.PROCESS_TASK : undefined,
      canLookupRootTask ? LookupStepType.FLOWCHART_PROCESS_TASK : undefined,
    ]),
    intermediateLookupStepTypes: compact([
      ...DEFAULT_INTERMEDIATE_LOOKUP_STEP_TYPES,
      canLookupRootTask ? LookupStepType.FLOWCHART_PROCESS_TASK : undefined,
    ]),
    finalLookupStepTypes: DEFAULT_FINAL_LOOKUP_STEP_TYPES,
    onRequestSubmit: ({lookupConfig}: {lookupConfig: LookupConfigSpec}) => {
      onSubmit(lookupConfig);
    },
    getLookupConfigValidationError: (
      inferredType: InferredLookupConfigType,
      lookupConfig: LookupConfigSpec
    ) => {
      const lookupConfigValidationMessage = getLookupConfigValidationMessage(
        inferredType,
        toSchemaField,
        allSchemasById,
        selectorsById
      );
      if (lookupConfigValidationMessage != null) {
        return lookupConfigValidationMessage;
      }
      return getAdditionalLookupConfigValidationMessage?.(lookupConfig) ?? null;
    },
    submitButtonText: 'Done',
  };

  assert(
    isLookupConfigValidForModal(runLookupConfigModalParams),
    'Lookup config is not supported in the lookup modal',
    {
      runLookupConfigModalParams,
    }
  );
  legacyRunModal(LookupConfigModal, runLookupConfigModalParams);
}

export default function SchemaFieldLookup(props: SchemaFieldLookupProps): JSX.Element {
  const {allSchemasById} = useAllSchemasById();
  if (props.lookupConfig) {
    return (
      <div className={css(styles.lookupCellContainer)}>
        <EditableLookupConfigDisplay
          baseSchema={props.fromSchema}
          isDisabled={props.disabled}
          lookupConfig={props.lookupConfig}
          context={{flowchartTaskSchema: props.flowchartTaskSchema}}
          onEdit={() => {
            runLookupConfigModalForProcessSchemaMapper({
              ...props,
              fromSchema: props.fromSchema!,
              allSchemasById,
              canLookupSourceObjectBeModified: true,
            });
          }}
          onRemove={props.onRemove}
          lookupType="Schema field mapper"
          editTestAttrs={{
            component: 'SchemaFieldLookup',
            element: 'editButton',
          }}
          removeTestAttrs={{
            component: 'SchemaFieldLookup',
            element: 'removeButton',
          }}
          lookupConfigDescriptionTestAttrs={{
            component: 'SchemaFieldLookup',
            element: 'description',
          }}
        />
      </div>
    );
  } else if (props.disabled) {
    return (
      <span
        {...getTestProps({
          component: 'SchemaFieldLookup',
          element: 'description',
          key: 'ignoredText',
        })}
      >
        Ignored
      </span>
    );
  } else {
    return (
      <TooltipWrapper tooltipTitle={props.fromSchema == null ? 'A source must be configured' : null}>
        <Button
          size={ButtonSize.SMALL}
          buttonStyle={ButtonStyle.TERTIARY_GRAY}
          disabled={props.fromSchema == null}
          onClick={() => {
            runLookupConfigModalForProcessSchemaMapper({
              ...props,
              fromSchema: props.fromSchema!,
              allSchemasById,
              canLookupSourceObjectBeModified: true,
            });
          }}
          text="Configure mapping"
          dataTestAttrs={{
            component: 'SchemaFieldLookup',
            element: 'configureMappingButton',
          }}
        />
      </TooltipWrapper>
    );
  }
}

const styles = StyleSheet.create({
  lookupCellContainer: {
    whiteSpace: 'normal',
  },
});
